import { Link } from "react-router-dom";
import { ArrowRight, Handshake, Factory, Cpu, Bot, Wrench, Drill, Flame } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

export default function TrustedPartners() {
  return (
    <div data-testid="partners-page">
      <Seo
        title="Trusted Partners"
        path="/partners"
        description="The companies, organizations, and industry relationships that help Slatech Solutions deliver value — a growing network of trusted manufacturers, suppliers, contractors, and service providers across North America."
      />

      {/* HERO */}
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1524514587686-e2909d726e9b"
            alt="Industrial gears connecting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-blue-500 mb-5">The Network</p>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-black uppercase tracking-tight text-white leading-[0.9] max-w-4xl">
              Trusted Partners.
            </h1>
            <p className="mt-6 font-heading text-xl sm:text-2xl uppercase tracking-wide text-zinc-200 max-w-3xl">
              Strong relationships. Proven expertise. Shared commitment to solving challenges.
            </p>
            <p className="mt-6 text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed">
              Slatech Solutions works alongside a growing network of trusted manufacturers, suppliers,
              contractors, and service providers across North America. These relationships help us
              identify opportunities, connect solutions, and create value for our customers.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SECTION 1 — AFFILIATED COMPANIES */}
      <section data-testid="affiliated-section" className="bg-background py-20 md:py-28 bg-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-3xl">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-blue-500 mb-4">Section 01</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-[0.95]">
              Affiliated Companies
            </h2>
            <p className="text-zinc-300 mt-5 text-lg leading-relaxed">
              Companies and organizations closely affiliated with Slatech Solutions through ongoing
              working relationships.
            </p>
          </Reveal>

          <Reveal delay={120} className="mt-14 border border-white/10 bg-zinc-950 p-10 md:p-16 text-center">
            <span className="inline-flex text-blue-500"><Handshake size={44} strokeWidth={1.5} /></span>
            <h3 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-white mt-6 leading-tight">
              Actively seeking new partnerships!
            </h3>
            <p className="font-heading text-xl uppercase tracking-wide text-zinc-200 mt-4">
              Interested?
            </p>
            <p className="text-zinc-300 mt-3 text-lg leading-relaxed max-w-xl mx-auto">
              Send us a note. We'd love to have a conversation.
            </p>
            <Link
              to="/partners/connect"
              data-testid="affiliated-cta"
              className="mt-8 inline-flex items-center justify-center gap-2.5 font-heading text-base font-semibold uppercase tracking-widest bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 transition-colors duration-200 group"
            >
              Send Us a Note
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WHAT WE'RE LOOKING FOR */}
      <section data-testid="looking-for-section" className="bg-background border-t border-white/10 py-20 md:py-28 bg-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-3xl">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-blue-500 mb-4">Opportunities</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-[0.95]">
              What We're Looking For
            </h2>
            <p className="text-zinc-300 mt-5 text-lg leading-relaxed">
              We're particularly interested in connecting with partners across these areas:
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-14">
            {[
              { label: "Industrial Equipment Manufacturers", icon: Factory },
              { label: "CNC & Machine Tool Suppliers", icon: Cpu },
              { label: "Automation & Robotics Providers", icon: Bot },
              { label: "Welding & Fabrication Solutions", icon: Wrench },
              { label: "Drilling & Oilfield Technologies", icon: Drill },
              { label: "Heat Treatment & Induction Systems", icon: Flame },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal
                  key={item.label}
                  delay={(i % 3) * 90}
                  className="group bg-zinc-950 hover:bg-zinc-900 p-8 transition-colors duration-300 flex items-start gap-5"
                >
                  <span className="text-blue-500 group-hover:text-blue-400 transition-colors duration-300 shrink-0">
                    <Icon size={32} strokeWidth={1.5} />
                  </span>
                  <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white leading-tight mt-1">
                    {item.label}
                  </h3>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — OUR APPROACH */}
      <section data-testid="approach-section" className="relative bg-background border-t border-white/10">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1669003154400-053af9a35eba')" }}
        />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-blue-500 mb-5">Our Approach</p>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight text-white leading-[0.95] max-w-4xl">
              Understanding the Challenge.
              <span className="block text-blue-500">Identifying the Opportunity.</span>
              Connecting the Solution.
            </h2>
            <p className="text-zinc-300 mt-8 text-lg leading-relaxed max-w-2xl">
              Every relationship on this page exists for one reason: helping customers find the right
              solution. Slatech Solutions believes strong partnerships create better outcomes, stronger
              businesses, and long-term success.
            </p>
          </Reveal>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section data-testid="partners-cta" className="bg-black py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight text-white leading-[0.95]">
              Ready to Connect?
            </h2>
            <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-2xl mx-auto">
              Whether you're looking for equipment, industry expertise, strategic partnerships, or
              business development opportunities, we'd welcome the conversation.
            </p>
            <Link
              to="/contact"
              data-testid="partners-cta-button"
              className="mt-10 inline-flex items-center justify-center gap-2.5 font-heading text-base font-semibold uppercase tracking-widest bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 transition-colors duration-200 group"
            >
              Get In Touch
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
